import { Send } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import { getSupabaseClient } from "../../atomic-crm/providers/supabase/supabase";

interface Turn {
  role: "visitor" | "assistant";
  body: string;
}

/**
 * The visitor-facing chat widget (/#/w/:key), designed to be embedded in an
 * iframe on the business's own website.
 *
 * The assistant answers only from the workspace's approved information, so
 * the page never receives that text: it sends a message and renders the
 * reply. Leaving contact details creates a CRM lead WITHOUT granting
 * marketing consent, which the composer's consent gate continues to
 * enforce separately.
 */
export const ChatWidgetPage = () => {
  const { key = "" } = useParams();
  const [widget, setWidget] = useState<
    { greeting: string; business_name: string } | null | "missing"
  >(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [showLead, setShowLead] = useState(false);
  const [leadName, setLeadName] = useState("");
  const [leadEmail, setLeadEmail] = useState("");
  const [leadPhone, setLeadPhone] = useState("");
  const [leadDone, setLeadDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getSupabaseClient()
      .rpc("get_chat_widget", { p_key: key })
      .then(
        ({ data }) => {
          if (!data) {
            setWidget("missing");
            return;
          }
          const config = data as { greeting: string; business_name: string };
          setWidget(config);
          setTurns([{ role: "assistant", body: config.greeting }]);
        },
        () => setWidget("missing"),
      );
  }, [key]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [turns.length, showLead]);

  const ensureSession = async () => {
    if (sessionId) return sessionId;
    const { data, error: rpcError } = await getSupabaseClient().rpc(
      "start_chat_session",
      { p_key: key },
    );
    if (rpcError) throw new Error(rpcError.message);
    setSessionId(data as string);
    return data as string;
  };

  const send = async () => {
    const message = draft.trim();
    if (!message || busy) return;
    setDraft("");
    setError(null);
    setTurns((prev) => [...prev, { role: "visitor", body: message }]);
    setBusy(true);
    try {
      const id = await ensureSession();
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chat-widget`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ key, session_id: id, message }),
        },
      );
      const payload = (await response.json()) as {
        reply?: string;
        error?: string;
      };
      if (!response.ok || !payload.reply) {
        throw new Error(payload.error ?? "The chat is unavailable right now.");
      }
      setTurns((prev) => [
        ...prev,
        { role: "assistant", body: payload.reply as string },
      ]);
      if (!leadDone) setShowLead(true);
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "The chat is unavailable right now.",
      );
    } finally {
      setBusy(false);
    }
  };

  const submitLead = async () => {
    setError(null);
    try {
      const id = await ensureSession();
      const { error: rpcError } = await getSupabaseClient().rpc(
        "capture_chat_lead",
        {
          p_session: id,
          p_name: leadName,
          p_email: leadEmail,
          p_phone: leadPhone,
        },
      );
      if (rpcError) throw new Error(rpcError.message);
      await getSupabaseClient().rpc("request_chat_handoff", { p_session: id });
      setLeadDone(true);
      setShowLead(false);
      setTurns((prev) => [
        ...prev,
        {
          role: "assistant",
          body: "Thank you. Someone from the team will follow up shortly.",
        },
      ]);
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Could not send that.",
      );
    }
  };

  if (widget === null) return null;
  if (widget === "missing") {
    return (
      <div className="flex h-screen items-center justify-center bg-background p-6 text-center">
        <p className="text-[14px] text-muted-foreground">
          This chat is not available.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col bg-background">
      <div className="border-b border-border-subtle px-4 py-3">
        <p className="text-[14px] font-semibold">{widget.business_name}</p>
        <p className="text-[11.5px] text-muted-foreground">
          Answers come from our team's information. A person reviews anything we
          cannot answer.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3">
        <div className="flex flex-col gap-2">
          {turns.map((turn, index) => (
            <div
              key={`${turn.role}-${index}`}
              className={
                turn.role === "visitor"
                  ? "self-end rounded-2xl rounded-br-sm bg-primary px-3 py-2 text-[14px] text-primary-foreground"
                  : "self-start rounded-2xl rounded-bl-sm bg-muted px-3 py-2 text-[14px]"
              }
              style={{ maxWidth: "85%" }}
            >
              {turn.body}
            </div>
          ))}
          {busy ? (
            <span className="self-start text-[12px] text-muted-foreground">
              Typing…
            </span>
          ) : null}

          {showLead && !leadDone ? (
            <div className="mt-2 flex flex-col gap-2 rounded-xl border border-border-subtle p-3">
              <span className="text-[13px] font-medium">
                Want someone to follow up?
              </span>
              <input
                className="h-10 rounded-lg border border-input bg-card px-3 text-[14px] outline-none"
                placeholder="Your name"
                value={leadName}
                onChange={(e) => setLeadName(e.target.value)}
              />
              <input
                className="h-10 rounded-lg border border-input bg-card px-3 text-[14px] outline-none"
                placeholder="Email"
                type="email"
                value={leadEmail}
                onChange={(e) => setLeadEmail(e.target.value)}
              />
              <input
                className="h-10 rounded-lg border border-input bg-card px-3 text-[14px] outline-none"
                placeholder="Phone"
                value={leadPhone}
                onChange={(e) => setLeadPhone(e.target.value)}
              />
              <button
                type="button"
                className="h-10 rounded-lg bg-primary text-[14px] font-medium text-primary-foreground"
                onClick={submitLead}
              >
                Ask for a callback
              </button>
              <button
                type="button"
                className="text-[12px] text-muted-foreground underline-offset-2 hover:underline"
                onClick={() => setShowLead(false)}
              >
                Keep chatting instead
              </button>
            </div>
          ) : null}

          {error ? (
            <span className="self-start text-[12px] text-destructive">
              {error}
            </span>
          ) : null}
          <div ref={endRef} />
        </div>
      </div>

      <div className="flex items-center gap-2 border-t border-border-subtle p-3">
        <input
          className="h-11 flex-1 rounded-full border border-input bg-card px-4 text-[14px] outline-none focus-visible:ring-2 focus-visible:ring-ring"
          placeholder="Type your question"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void send();
            }
          }}
        />
        <button
          type="button"
          aria-label="Send"
          className="flex size-11 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground disabled:opacity-50"
          disabled={busy || !draft.trim()}
          onClick={() => void send()}
        >
          <Send className="size-4" />
        </button>
      </div>
    </div>
  );
};

ChatWidgetPage.path = "/w/:key";
